import { z } from 'zod';

export const uuidSchema = z.string().uuid();

export const localeSchema = z.enum(['tr', 'en', 'de', 'fr', 'nl', 'pl', 'it', 'es', 'sv', 'pt']);
export type Locale = z.infer<typeof localeSchema>;

export const currencyCodeSchema = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/, 'ISO 4217 üç harfli para birimi kodu gerekli');
export type CurrencyCode = z.infer<typeof currencyCodeSchema>;

export const countryCodeSchema = z
  .string()
  .length(2)
  .regex(/^[A-Z]{2}$/, 'ISO 3166-1 alpha-2 ülke kodu gerekli (örn. DE, FR, NL, TR)');
export type CountryCode = z.infer<typeof countryCodeSchema>;

function normalizeIban(v: unknown): unknown {
  if (typeof v === 'string') return v.replace(/\s+/g, '').toUpperCase();
  return v;
}

function ibanMod97(iban: string): number {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged
    .split('')
    .map((ch) => (ch >= 'A' && ch <= 'Z' ? (ch.charCodeAt(0) - 55).toString() : ch))
    .join('');
  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 7) {
    const chunk = remainder.toString() + numeric.slice(i, i + 7);
    remainder = parseInt(chunk, 10) % 97;
  }
  return remainder;
}

export const ibanSchema = z.preprocess(
  normalizeIban,
  z
    .string()
    .min(15)
    .max(34)
    .regex(/^[A-Z]{2}\d{2}[A-Z0-9]+$/, 'IBAN formatı hatalı')
    .refine((v) => ibanMod97(v) === 1, 'IBAN doğrulaması başarısız'),
);
export type Iban = z.infer<typeof ibanSchema>;

export const bicSchema = z
  .string()
  .regex(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'BIC/SWIFT formatı hatalı (8 veya 11 karakter)');
export type Bic = z.infer<typeof bicSchema>;

function normalizeDecimal(v: unknown): unknown {
  if (typeof v === 'string') return v.trim().replace(',', '.');
  return v;
}

export const decimalStringSchema = z.preprocess(
  normalizeDecimal,
  z.string().regex(/^-?\d+(\.\d{1,2})?$/, 'Geçerli ondalık değer gerekli (2 basamak)'),
);

export const positiveDecimalSchema = z.preprocess(
  normalizeDecimal,
  z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, 'Pozitif ondalık gerekli')
    .refine((v) => parseFloat(v) > 0, 'Sıfırdan büyük olmalı'),
);

export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(20),
});

export const yearMonthSchema = z.object({
  year: z.coerce.number().int().min(1970).max(2100),
  month: z.coerce.number().int().min(1).max(12),
});

export const isoDateSchema = z.string().date();
export const isoDateTimeSchema = z.string().datetime();

/**
 * Form `<select>`'lerinin boş seçeneği ('') → null. `z.preprocess(emptyToNull, …nullable())`
 * ile kullanılır: boş seçim "temizle/seçilmedi" anlamına gelir ve doğrulamayı sessizce
 * düşürmez (RHF handleSubmit eskiden '' yüzünden hiç çağrılmıyordu).
 */
export function emptyToNull(v: unknown): unknown {
  return v === '' ? null : v;
}
